import { SelectList, SelectListEvent } from '@acies/core';
import { findGeometryParameter } from '../../../domain/configurations';
import { GeometryShapeTypes, type IGeometryShapeType } from '../../../domain/geometry-shape-types';
import { ConfiguratorModel } from '../configurator.model';
import GeometrySelectListEvent from './geometry-select-list-event';
import GeometrySelectListItemRenderer from './geometry-select-list-item-renderer';

/**
 * The shapes a window can be cut to, for the user to pick one from.
 *
 * Its items are the shape types themselves rather than objects describing them, so the shape a configuration answers with can be selected as it is: a string is the same item wherever it came from.
 *
 * Every shape there is, in the order the engine lists them, set here rather than handed in: the list is fixed in the domain and never changes, so there is no state for anyone else to keep. Copied, because the domain holds it as a readonly tuple and a list takes an array of its own.
 *
 * Which of them is selected follows the position being worked on: the list finds the geometry parameter among `ConfiguratorModel.currentParameters` itself, the way `DrawingViewer` follows the drawing, so the dialog it sits in only decides where it goes.
 *
 * Laid out as a grid of four shapes to a row, and as big as those rows rather than as big as the room it is given: its columns are as wide as a shape, so the shapes sit exactly a gap apart. No padding of its own, since the geometry dialog's content already has it.
 *
 * Never left without a selection: a window is always cut to some shape, so picking the selected one again leaves it selected — see `SelectList.allowDeselect`.
 *
 * Says itself which shape it shows selected, with a {@link GeometrySelectListEvent} of type SHAPE_SELECTED, whoever selected it. A shape the user picks is answered with one of type GEOMETRY_CHANGED as well; see #selectionChanged for how a pick is told apart from a shape the list selected itself.
 */
export default class GeometrySelectList extends SelectList<IGeometryShapeType> {
	public constructor() {
		super();
		this.width = 'hug';
		this.height = 'hug';
		this.paddingHorizontal = 0;
		this.paddingVertical = 0;
		// List clips and scrolls by default, but this one is as big as its shapes and has nothing to scroll
		// to; clipped, the focus stroke a shape draws on its edge would be cut off along the list's own
		this.clipContent = false;
		this.autoLayout = 'grid';
		this.gridColumnCount = 4;
		this.gapHorizontal = 12;
		this.gapVertical = 12;
		this.allowDeselect = false;
		this.ItemRenderer = GeometrySelectListItemRenderer;
		this.data = [...GeometryShapeTypes.literals];
		// the listener is registered on the model, so it would otherwise run in its scope rather than
		// in this one
		this.parametersChanged = this.parametersChanged.bind(this);
		// Subscribed here rather than on connect, because an ObservableCollection tells its listeners
		// about a change and not about what it already holds. What it already holds is taken straight
		// after, so a list built after a position was selected selects its shape all the same.
		ConfiguratorModel.currentParameters.subscribe(this.parametersChanged);
		this.parametersChanged();
	}

	/** The SelectListEvent is caught here rather than left to bubble, so the list is the only place that speaks about list items. */
	protected override initialize(): void {
		super.initialize();
		this.addEventListener(SelectListEvent.SELECTION_CHANGED, this.#selectionChanged as EventListener);
	}

	/**
	 * Unsubscribes, because the model outlives the list.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a list that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentParameters.unsubscribe(this.parametersChanged);
	}

	/**
	 * Says which shape the list now shows selected, and answers a shape the user picked with a GeometrySelectListEvent of its own.
	 *
	 * The SelectList raises a SelectListEvent for every selection, including the ones this list makes itself to show what the position is cut to: it selects by clicking, so it has no way of telling them apart. The model does. A shape the model already holds is one this list selected from it, or one the user picked
	 * back; neither is a change, and answering it would ask for a configuration of what the position already is — whose answer would select that same shape again. So only a shape other than the model's is said as a GEOMETRY_CHANGED.
	 *
	 * Every selection is said as a SHAPE_SELECTED first, whoever made it, because what sits beside the list follows the shape on show rather than the shape picked: the measurements are only shown while the list is on the shape the position is cut to, and the user picking that shape back is exactly when they come back.
	 *
	 * Null is the list before it has selected anything, which is nothing to say either.
	 *
	 * The SelectListEvent is stopped either way, because it has been dealt with here.
	 */
	#selectionChanged(event: SelectListEvent<IGeometryShapeType | null>): void {
		event.stopPropagation();
		const shapeType = event.selectedItem;
		if (shapeType === null) {
			return;
		}
		this.dispatchEvent(new GeometrySelectListEvent(shapeType, GeometrySelectListEvent.SHAPE_SELECTED));
		if (shapeType === findGeometryParameter(ConfiguratorModel.currentParameters.collection)?.shapeType) {
			return;
		}
		this.dispatchEvent(new GeometrySelectListEvent(shapeType));
	}

	/**
	 * Selects the shape the position being worked on is cut to.
	 *
	 * Left alone when the parameters hold no geometry — no position selected, one that is not a configured model, or a model without a geometry. The list may not be emptied, so it keeps the shape it had, and it is never seen that way: the button that opens the dialog is only pressable while there is a geometry to
	 * show. See `ShowGeometryDialogButton`.
	 */
	private parametersChanged(): void {
		const geometry = findGeometryParameter(ConfiguratorModel.currentParameters.collection);
		if (geometry !== null) {
			this.selectedItem = geometry.shapeType;
		}
	}
}
customElements.define('cf-geometry-select-list', GeometrySelectList);
